import {ZodError, type PackageJSON} from '@sanity/parse-package-json'
import chalk from 'chalk'
import type {Logger} from '../../logger.ts'
import type {StrictOptions} from '../../strict.ts'
import {assertLast, assertOrder} from './helpers.ts'
import {loadPkg} from './loadPkg.ts'

/**
 * Helper function to recursively compare export values, excluding source, development, and monorepo conditions
 */
function areExportValuesEqual(value1: unknown, value2: unknown): boolean {
  // If both are strings, simple comparison
  if (typeof value1 === 'string' && typeof value2 === 'string') {
    return value1 === value2
  }

  // If types don't match, they're not equal
  if (typeof value1 !== typeof value2) {
    return false
  }

  // Both are objects, compare recursively
  if (
    typeof value1 === 'object' &&
    value1 !== null &&
    typeof value2 === 'object' &&
    value2 !== null &&
    !Array.isArray(value1) &&
    !Array.isArray(value2)
  ) {
    const obj1 = value1 as Record<string, any>
    const obj2 = value2 as Record<string, any>

    const keys1 = Object.keys(obj1).filter(
      (k) => k !== 'source' && k !== 'development' && k !== 'monorepo',
    )
    const keys2 = Object.keys(obj2).filter(
      (k) => k !== 'source' && k !== 'development' && k !== 'monorepo',
    )

    // Check if they have the same keys
    if (keys1.length !== keys2.length) {
      return false
    }

    for (const key of keys1) {
      if (!keys2.includes(key)) {
        return false
      }

      const val1 = obj1[key]
      const val2 = obj2[key]

      // Skip if either value is undefined
      if (val1 === undefined || val2 === undefined) {
        return false
      }

      // Recursively compare nested values
      if (!areExportValuesEqual(val1, val2)) {
        return false
      }
    }

    return true
  }

  return false
}

/** @alpha */
export async function loadPkgWithReporting(options: {
  pkgPath: string
  logger: Logger
  strict: boolean
  strictOptions: StrictOptions
}): Promise<PackageJSON> {
  const {pkgPath, logger, strict, strictOptions} = options

  try {
    const pkg = await loadPkg({pkgPath})
    let shouldError = false

    if (strict) {
      // Check for missing or commonjs type field
      if (strictOptions.preferModuleType !== 'off') {
        if (!pkg.type) {
          const msg =
            'package.json: `type` field is missing. Future versions of pkg-utils will require `"type": "module"`. Consider adding `"type": "module"` to prepare for this change.'
          if (strictOptions.preferModuleType === 'error') {
            shouldError = true
            logger.error(msg)
          } else {
            logger.warn(msg)
          }
        } else if (pkg.type === 'commonjs') {
          const msg =
            'package.json: `type` is set to "commonjs". Future versions of pkg-utils will require `"type": "module"`. Consider migrating to ES modules to prepare for this change.'
          if (strictOptions.preferModuleType === 'error') {
            shouldError = true
            logger.error(msg)
          } else {
            logger.warn(msg)
          }
        }
      }

      // Check for banned root-level fields
      if (strictOptions.noPackageJsonBrowser !== 'off' && pkg.browser) {
        const msg =
          'package.json: the `browser` field is no longer needed. Use the `browser` condition in `exports` instead for better support across modern bundlers.'
        if (strictOptions.noPackageJsonBrowser === 'error') {
          shouldError = true
          logger.error(msg)
        } else {
          logger.warn(msg)
        }
      }

      if (strictOptions.noPackageJsonTypesVersions !== 'off' && pkg.typesVersions) {
        const msg =
          'package.json: the `typesVersions` field is no longer needed. TypeScript has long supported conditional exports and the `types` condition. Remove the `typesVersions` field and use the `types` condition in `exports` instead.'
        if (strictOptions.noPackageJsonTypesVersions === 'error') {
          shouldError = true
          logger.error(msg)
        } else {
          logger.warn(msg)
        }
      }
    }

    // validate exports
    if (pkg.exports) {
      const _exports = Object.entries(pkg.exports)

      for (const [expPath, exp] of _exports) {
        if (typeof exp === 'string' || 'svelte' in exp) {
          continue
        }

        const keys = Object.keys(exp)

        if (exp.types) {
          shouldError = true
          logger.error(
            `exports["${expPath}"]: the \`types\` condition shouldn't be used as dts files are generated in such a way that both CJS and ESM is supported`,
          )
        }

        if (exp.module) {
          shouldError = true
          logger.error(
            `exports["${expPath}"]: the \`module\` condition shouldn't be used as it's not well supported in all bundlers.`,
          )
        }

        if (exp.development && exp.source && exp.development !== exp.source) {
          shouldError = true
          logger.error(
            `exports["${expPath}"]: the \`development\` condition must have the same value as \`source\` when both are present. Expected "${exp.source}" but got "${exp.development}"`,
          )
        }

        if (exp.monorepo && exp.source && exp.monorepo !== exp.source) {
          shouldError = true
          logger.error(
            `exports["${expPath}"]: the \`monorepo\` condition must have the same value as \`source\` when both are present. Expected "${exp.source}" but got "${exp.monorepo}"`,
          )
        }

        if (exp.node) {
          if (exp.import && exp.node.import && !assertOrder('node', 'import', keys)) {
            shouldError = true
            logger.error(
              `exports["${expPath}"]: the \`node\` property should come before the \`import\` property`,
            )
          }

          if (exp.node.module) {
            shouldError = true
            logger.error(
              `exports["${expPath}"]: the \`node.module\` condition shouldn't be used as it's not well supported in all bundlers. A better strategy is to refactor the codebase to no longer be vulnerable to the "dual package hazard"`,
            )
          }

          if (
            !exp.node.source &&
            exp.node.import &&
            (exp.node.require || exp.require) &&
            (exp.node.import.endsWith('.cjs.js') || exp.node.import.endsWith('.cjs.mjs'))
          ) {
            shouldError = true
            logger.error(
              `exports["${expPath}"]: the \`node.import\` re-export pattern shouldn't be used as it's not well supported in all bundlers. A better strategy is to refactor the codebase to no longer be vulnerable to the "dual package hazard"`,
            )
          }

          if (exp.require && exp.node.require && exp.require === exp.node.require) {
            shouldError = true
            logger.error(
              `exports["${expPath}"]: the \`node.require\` property isn't necessary as it's identical to \`require\``,
            )
          } else if (exp.require && exp.node.require && !assertOrder('node', 'require', keys)) {
            shouldError = true
            logger.error(
              `exports["${expPath}"]: the \`node\` property should come before the \`require\` property`,
            )
          }
        } else {
          if (!assertOrder('import', 'require', keys)) {
            logger.warn(
              `exports["${expPath}"]: the \`import\` property should come before the \`require\` property`,
            )
          }
        }

        if (!assertLast('default', keys)) {
          shouldError = true
          logger.error(
            `exports["${expPath}"]: the \`default\` property should be the last property`,
          )
        }
      }
    }

    // validate publishConfig.exports
    if (strict && pkg.exports && Object.keys(pkg.exports).length > 0) {
      // Check if exports contains source, development, or monorepo conditions
      const hasSourceOrDevelopment = Object.entries(pkg.exports).some(([, exp]) => {
        if (typeof exp === 'string') return false
        if (typeof exp === 'object' && 'svelte' in exp) return false
        return Boolean(exp.source || exp.development || exp.monorepo)
      })

      if (hasSourceOrDevelopment) {
        if (!pkg.publishConfig?.exports) {
          const msg =
            'package.json: `publishConfig.exports` is missing. Adding it helps avoid publishing to npm with the `source`, `development`, or `monorepo` condition that points to code that cannot be used by the resolver. ' +
            'See https://tsdown.dev/options/package-exports#dev-exports for more information.'
          if (strictOptions.noPublishConfigExports === 'error') {
            shouldError = true
            logger.error(msg)
          } else if (strictOptions.noPublishConfigExports !== 'off') {
            logger.warn(msg)
          }
        } else {
          // Validate publishConfig.exports structure
          const publishExports = pkg.publishConfig.exports

          // Check that all keys in exports exist in publishConfig.exports
          for (const exportPath of Object.keys(pkg.exports)) {
            if (!(exportPath in publishExports)) {
              shouldError = true
              logger.error(
                `publishConfig.exports: missing export path "${exportPath}" that exists in exports`,
              )
            }
          }

          // Check that all keys in publishConfig.exports exist in exports
          for (const exportPath of Object.keys(publishExports)) {
            if (!(exportPath in pkg.exports)) {
              shouldError = true
              logger.error(
                `publishConfig.exports: unexpected export path "${exportPath}" that does not exist in exports`,
              )
            }
          }

          // Validate each export path
          for (const [exportPath, exp] of Object.entries(pkg.exports)) {
            if (typeof exp === 'string' || 'svelte' in exp) {
              // For string or svelte exports, publishConfig should match
              const publishExp = publishExports[exportPath]
              if (
                typeof publishExp !== 'string' &&
                (typeof publishExp !== 'object' || !('svelte' in publishExp))
              ) {
                shouldError = true
                logger.error(
                  `publishConfig.exports["${exportPath}"]: should be a string matching exports["${exportPath}"]`,
                )
              }
              continue
            }

            const publishExp = publishExports[exportPath]
            if (!publishExp) {
              continue
            }
            if (typeof publishExp === 'string') {
              // publishConfig has a string, validate it's correct
              // It should be a condensed form when only default remains after removing source/development/monorepo
              const conditions = Object.keys(exp).filter(
                (k) => k !== 'source' && k !== 'development' && k !== 'monorepo',
              )
              if (conditions.length !== 1 || conditions[0] !== 'default') {
                shouldError = true
                logger.error(
                  `publishConfig.exports["${exportPath}"]: is a string but exports["${exportPath}"] has multiple conditions besides source/development/monorepo: ${conditions.join(', ')}`,
                )
              } else {
                // Validate that the string value matches the default condition value
                const expectedValue = exp.default
                if (publishExp !== expectedValue) {
                  shouldError = true
                  logger.error(
                    `publishConfig.exports["${exportPath}"]: should be "${expectedValue}" but got "${publishExp}"`,
                  )
                }
              }
              continue
            }

            if ('svelte' in publishExp) {
              continue
            }

            // Validate conditions
            const exportConditions = Object.keys(exp).filter(
              (k) => k !== 'source' && k !== 'development' && k !== 'monorepo',
            )
            const publishConditions = Object.keys(publishExp)

            // Check for source, development, or monorepo in publishConfig
            if ('source' in publishExp) {
              shouldError = true
              logger.error(
                `publishConfig.exports["${exportPath}"]: should not contain the \`source\` condition`,
              )
            }

            if ('development' in publishExp) {
              shouldError = true
              logger.error(
                `publishConfig.exports["${exportPath}"]: should not contain the \`development\` condition`,
              )
            }

            if ('monorepo' in publishExp) {
              shouldError = true
              logger.error(
                `publishConfig.exports["${exportPath}"]: should not contain the \`monorepo\` condition`,
              )
            }

            // Check that all conditions match (except source/development/monorepo)
            for (const condition of exportConditions) {
              if (!(condition in publishExp)) {
                shouldError = true
                logger.error(
                  `publishConfig.exports["${exportPath}"]: missing \`${condition}\` condition that exists in exports["${exportPath}"]`,
                )
              }
            }

            for (const condition of publishConditions) {
              if (!exportConditions.includes(condition)) {
                shouldError = true
                logger.error(
                  `publishConfig.exports["${exportPath}"]: unexpected \`${condition}\` condition that does not exist in exports["${exportPath}"]`,
                )
              }
            }

            // Validate that values match for all conditions
            for (const condition of exportConditions) {
              if (condition in publishExp) {
                const exportValue = (exp as Record<string, unknown>)[condition]
                const publishValue = (publishExp as Record<string, unknown>)[condition]

                // Compare values recursively for nested objects
                if (!areExportValuesEqual(exportValue, publishValue)) {
                  const exportValueStr =
                    typeof exportValue === 'string' ? exportValue : JSON.stringify(exportValue)
                  const publishValueStr =
                    typeof publishValue === 'string' ? publishValue : JSON.stringify(publishValue)
                  shouldError = true
                  logger.error(
                    `publishConfig.exports["${exportPath}"].${condition}: should be ${exportValueStr} but got ${publishValueStr}`,
                  )
                }
              }
            }
          }
        }
      }
    }

    if (shouldError) {
      process.exit(1)
    }

    return pkg
  } catch (err) {
    if (err instanceof ZodError) {
      for (const issue of err.issues) {
        if (issue.code === 'invalid_type') {
          logger.error(
            [
              `\`${formatPath(issue.path)}\` `,
              `in \`./package.json\` must be of type ${chalk.magenta(issue.expected)} `,
              `(received ${chalk.magenta(issue.received)})`,
            ].join(''),
          )
          continue
        }

        logger.error(issue)
      }
    } else {
      logger.error(err)
    }

    return process.exit(1)
  }
}

function formatPath(segments: Array<string | number>) {
  return segments
    .map((s, idx) => {
      if (idx === 0) return s

      if (typeof s === 'number') {
        return `[${s}]`
      }

      if (s.startsWith('.')) {
        return `["${s}"]`
      }

      return `.${s}`
    })
    .join('')
}
